import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { apiGet, apiPost, apiPut, streamChatMessage } from '@/lib/api';
import { Switch } from '@/components/ui/switch';
import { Send, Camera, Sparkles, X, Mic, ImagePlus, Paperclip, History, ThumbsUp, ThumbsDown } from 'lucide-react';
import { toast } from 'sonner';
import VoiceAIOverlay from '@/components/VoiceAIOverlay';
import { WatermarkedMedia } from '@/components/WatermarkedMedia';
import { buddyEvents } from '@/components/TutorBuddy';
import BuddyChatGreeting from '@/components/BuddyChatGreeting';
import RosterPicker from '@/components/RosterPicker';
import confetti from 'canvas-confetti';
import AhaSummaryCard from '@/components/AhaSummaryCard';
import StudySidebar from '@/components/study/StudySidebar';
import { Hint } from '@/components/ui/hint';
import { HELP } from '@/lib/helpText';
import { useScreenTip } from '@/hooks/useScreenTip';
import PremiumBoundary from '@/components/study/PremiumBoundary';
import ModePickerDialog from '@/components/study/ModePickerDialog';
import SyllabusUploadDialog from '@/components/study/SyllabusUploadDialog';
import SnapSolveDialog from '@/components/study/SnapSolveDialog';
import TodaysMissionCard from '@/components/study/TodaysMissionCard';
import { AcademicProfileDialog, needsAcademicProfile } from '@/components/study/AcademicProfileDialog';
import ThemeDialog from '@/components/profile/ThemeDialog';
import PlanChoiceDialog from '@/components/study/PlanChoiceDialog';
import IntroVideoDialog from '@/components/study/IntroVideoDialog';
import WelcomeFilm from '@/components/study/WelcomeFilm';
import GuideChips from '@/components/chat/GuideChips';
import FormattedMessage from '@/components/chat/FormattedMessage';
import TopicChips from '@/components/chat/TopicChips';
import AnswerChoices from '@/components/chat/AnswerChoices';
import SelectionSaver from '@/components/notes/SelectionSaver';
import { NOTE_EVENTS } from '@/lib/notes';
import { sameName } from '@/lib/nameKey';
import ChatHistoryDrawer from '@/components/chat/ChatHistoryDrawer';
import SyllabusMappingStep from '@/components/study/SyllabusMappingStep';
import { useHelpCenter } from '@/lib/helpCenter';
import { SyllabusMappingPanel } from '@/components/study/SyllabusMappingPanel';
import GuidedSessionPanel from '@/components/study/GuidedSessionPanel';
import ExamModeWizard from '@/components/premium/ExamModeWizard';
import { fetchExamModeReadiness } from '@/lib/premiumExams';
import ExamModeGate from '@/components/premium/ExamModeGate';
import {
  MODES, getStoredMode, setStoredMode, fetchModes,
  fetchActiveSession, startGuidedSession,
} from '@/lib/studyModes';

export default function HomePage({ activeSubject, chatResetKey = 0, activeTopic: headerTopic = '', onSessionLoad }) {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [rating, setRating] = useState({});          // message_id -> 'up' | 'down'

  const [activeSession, setActiveSession] = useState(null);
  const [input, setInput] = useState('');
  const [eli5, setEli5] = useState(false);
  useScreenTip('home-study', HELP.tips.chat);
  const [sending, setSending] = useState(false);
  // The first word normally lands in well under a second. When it genuinely
  // takes longer, say so instead of leaving a child watching three dots.
  const [thinkingNote, setThinkingNote] = useState('');
  const [streamStarted, setStreamStarted] = useState(false);
  const thinkTimer = useRef(null);
  const beginThinking = useCallback(() => {
    setStreamStarted(false); setThinkingNote('');
    clearTimeout(thinkTimer.current);
    thinkTimer.current = setTimeout(
      () => setThinkingNote('Gurukul Veda is thinking before it gives you the right answer\u2026'), 1200);
  }, []);
  const firstWordArrived = useCallback(() => {
    clearTimeout(thinkTimer.current); setThinkingNote(''); setStreamStarted(true);
  }, []);
  useEffect(() => () => clearTimeout(thinkTimer.current), []);
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState('');
  const [showSummary, setShowSummary] = useState(false);
  const [summary, setSummary] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);
  const [rosterOpen, setRosterOpen] = useState(false);
  const [studyNudgeShown, setStudyNudgeShown] = useState(false);

  // ===== Study Modes (Premium) =====
  // Mode is picked at session start and cleared whenever the student taps the
  // Gurukul Veda logo / Home (chatResetKey bump) so the picker returns.
  const [modes, setModes] = useState([]);
  const [premiumOff, setPremiumOff] = useState(true); // fail closed until confirmed
  const [mode, setMode] = useState(() => getStoredMode(user));
  const [modesLoaded, setModesLoaded] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [uploadSubject, setUploadSubject] = useState('');
  const isStudent = !['admin', 'support_staff', 'parent', 'teacher', 'school'].includes(user?.role);
  const [pickerOpen, setPickerOpen] = useState(false);
  // First-run setup: Mode Of Study (1) → School & Class (2) → Study theme (3).
  const [themeStepOpen, setThemeStepOpen] = useState(false);
  const [themeStepDone, setThemeStepDone] = useState(false);
  const [pendingSyllabus, setPendingSyllabus] = useState(false);
  const needsThemeSetup = isStudent && !user?.theme_setup_seen && !user?.theme?.preset;
  const [planStepOpen, setPlanStepOpen] = useState(false);
  const [planStepDone, setPlanStepDone] = useState(false);
  const needsPlanChoice = isStudent && !user?.plan_intent_seen;
  const [mappingStepOpen, setMappingStepOpen] = useState(false);
  const [mappingStepDone, setMappingStepDone] = useState(false);
  const needsMappingIntro = isStudent && !user?.syllabus_intro_seen;
  // Step 0 — the welcome video, when an admin has switched it on.
  // Step 0 — a real video when an admin has uploaded one, otherwise the in-app
  // animated Welcome Film, which ships with the app and is on by default.
  const help = useHelpCenter();
  const helpLoaded = !!help && Object.keys(help).length > 0;
  const [introDone, setIntroDone] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [filmReplay, setFilmReplay] = useState(false);
  const adminVideo = help?.intro_video_enabled === true && !!help?.intro_video_url;
  const step0Pending = isStudent && helpLoaded && !introDone && !user?.intro_video_seen;
  const introOpen = step0Pending && adminVideo;
  const filmOpen = step0Pending && !adminVideo && help?.welcome_film_enabled !== false;
  const finishIntro = async () => {
    setIntroDone(true);
    try { await apiPost('/intro-video/seen', {}); setUser({ ...user, intro_video_seen: true }); } catch { /* non-blocking */ }
  };
  const [guided, setGuided] = useState(null);
  const [guidedLoading, setGuidedLoading] = useState(false);
  const [examWizardOpen, setExamWizardOpen] = useState(false);
  // Exam Mode readiness: null = not checked yet. Exam Mode must not start a
  // single guided session until this says ready.
  const [examReady, setExamReady] = useState(null);
  const [examGateOpen, setExamGateOpen] = useState(false);
  const [examCheckSeq, setExamCheckSeq] = useState(0);

  useEffect(() => {
    fetchModes()
      .then(d => {
        setPremiumOff(d.premium_off);
        setModes(d.modes?.length ? d.modes : [
          { key: 'explore', name: 'Explore Mode', tagline: 'Learn at your own pace', icon: 'compass', gradient: 'from-sky-500 to-blue-600' },
          { key: 'ai_tutor', name: 'Exam Mode', tagline: 'Guided exam preparation', icon: 'crown', gradient: 'from-emerald-500 to-teal-600', locked: true },
        ]);
        setModesLoaded(true);
        // Reconcile the persisted choice: if the mode was killed by an admin
        // switch (or is no longer listed), fall back to Explore.
        const stored = getStoredMode(user);
        if (!d.premium_off && stored && !(d.modes || []).some(m => m.key === stored && !m.locked)) {
          setStoredMode('', user);
          setMode('');
          setGuided(null);
          if (!d.premium_off) setPickerOpen(true);
        }
      })
      .catch(() => { setPremiumOff(true); setModes([]); });
  }, []);

  // `mode` is seeded on first render, when the user object can still be null —
  // so the settled choice stored on the account is invisible at that moment.
  // Adopt it as soon as the user arrives, otherwise a student who has already
  // chosen Exam Mode is asked to choose again on every fresh login (and the
  // Exam Mode checks below never run, because the picker is open).
  useEffect(() => {
    if (mode || !user) return;
    const stored = getStoredMode(user);
    if (stored) setMode(stored);
  }, [user, mode]);

  // The mode is a settled preference (owner feedback: re-asking on every new
  // chat and every subject was annoying). We only ask when the student has
  // never chosen one — after that it stands until they tap the mode button.
  useEffect(() => {
    if (!modesLoaded || introOpen) return;
    if (!mode && user) setPickerOpen(true);
  }, [modesLoaded, mode, introOpen, user]);

  const pickMode = async (key) => {
    try {
      await apiPut('/school-mapping/study-preference', { study_mode: key });
      setMode(key); setStoredMode(key, user); setUser({ ...user, study_mode: key }); setPickerOpen(false);
      if (key === MODES.EXAM) setExamCheckSeq(n => n + 1);
    } catch (e) { toast.error(e.message || 'Could not save your Mode Of Study. Please try again.'); }
  };

  // Step 2 blocks Home until the school details are in; step 3 (theme) then
  // opens on its own and is the only skippable step.
  const academicOpen = isStudent && !!mode && !pickerOpen && (profileOpen || needsAcademicProfile(user));

  useEffect(() => {
    if (!isStudent || !mode || pickerOpen || needsAcademicProfile(user)) return;
    if (needsThemeSetup && !themeStepDone) setThemeStepOpen(true);
  }, [isStudent, mode, pickerOpen, user, needsThemeSetup, themeStepDone]);

  const finishThemeStep = () => {
    setThemeStepOpen(false);
    setThemeStepDone(true);
    if (needsPlanChoice) { setPlanStepOpen(true); return; }
    if (needsMappingIntro) { setMappingStepOpen(true); return; }
    if (pendingSyllabus) { setPendingSyllabus(false); setSyllabusOpen(true); }
  };

  // Step 4 — the plan that starts once the free trial ends.
  useEffect(() => {
    if (!isStudent || !mode || pickerOpen || needsAcademicProfile(user)) return;
    if (needsThemeSetup && !themeStepDone) return;
    if (needsPlanChoice && !planStepDone) setPlanStepOpen(true);
  }, [isStudent, mode, pickerOpen, user, needsThemeSetup, themeStepDone, needsPlanChoice, planStepDone]);

  const finishPlanStep = (res) => {
    setPlanStepOpen(false);
    setPlanStepDone(true);
    setUser({ ...user, plan_intent_seen: true, plan_intent_tier: res?.tier, plan_intent_cycle: res?.cycle });
    if (needsMappingIntro) { setMappingStepOpen(true); return; }
    if (pendingSyllabus) { setPendingSyllabus(false); setSyllabusOpen(true); }
  };

  // Step 5 — Study Buddy shows the mapping it built and invites feedback.
  useEffect(() => {
    if (!isStudent || !mode || pickerOpen || needsAcademicProfile(user)) return;
    if (needsThemeSetup && !themeStepDone) return;
    if (needsPlanChoice && !planStepDone) return;
    if (needsMappingIntro && !mappingStepDone) setMappingStepOpen(true);
  }, [isStudent, mode, pickerOpen, user, needsThemeSetup, themeStepDone,
      needsPlanChoice, planStepDone, needsMappingIntro, mappingStepDone]);

  const closeMappingStep = async (mismatch) => {
    setMappingStepOpen(false);
    setMappingStepDone(true);
    try { await apiPost('/school-mapping/intro-seen', {}); setUser({ ...user, syllabus_intro_seen: true }); } catch { /* non-blocking */ }
    if (mismatch || pendingSyllabus) { setPendingSyllabus(false); setUploadSubject(''); setSyllabusFile(null); setSyllabusOpen(true); }
  };

  // Exam Mode — check readiness BEFORE anything starts. An exam has to exist,
  // still be ahead of us, carry a mapped portion with paper dates and have a
  // plan. Until all four are true we show the gate pop-up and start nothing:
  // the chapter a child was exploring is very often not in the exam at all.
  useEffect(() => {
    if (mode !== MODES.EXAM) { setExamReady(null); setExamGateOpen(false); return; }
    if (premiumOff || !isStudent || pickerOpen || academicOpen) return;
    let cancelled = false;
    setGuided(null);
    fetchExamModeReadiness()
      .then(d => {
        if (cancelled) return;
        setExamReady(d);
        if (!d.ready) { setExamGateOpen(true); return; }
        // Move the whole screen onto the chapter the plan asks for, not the one
        // left over from Explore Mode.
        // ...unless a specific chat is open / being opened — that chat's chapter wins.
        const openingChat = activeSessionRef.current || syncingRef.current || searchParams.get('session') || openedFromUrl.current;
        if (d.focus?.subject && d.focus?.topic && !openingChat) {
          setSelectedTopic(d.focus.topic);
          onSessionLoad?.(d.focus.subject, d.focus.topic);
        }
      })
      .catch(() => { if (!cancelled) setExamReady(null); /* module off or not entitled — stay quiet */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [premiumOff, mode, isStudent, pickerOpen, academicOpen, examCheckSeq]);

  // Exam Mode — resume the open guided session or start today's. Gated on
  // readiness, and locked to the chapter the plan chose.
  useEffect(() => {
    if (premiumOff || mode !== MODES.EXAM || !activeSubject) return;
    if (!examReady?.ready) return;
    const planned = examReady.focus || {};
    if (planned.subject && planned.subject !== activeSubject) return;
    const topic = planned.topic || headerTopic || selectedTopic;
    if (!topic) return;
    let cancelled = false;
    setGuidedLoading(true);
    fetchActiveSession(activeSubject, topic)
      .then(d => (d.session ? d.session : startGuidedSession(activeSubject, topic)))
      .then(s => { if (!cancelled) setGuided(s); })
      .catch(() => { if (!cancelled) setGuided(null); })
      .finally(() => { if (!cancelled) setGuidedLoading(false); });
    return () => { cancelled = true; };
  }, [premiumOff, mode, activeSubject, headerTopic, selectedTopic, examReady]);

  // Iter 60n (Aug 2026) — Removed the on-demand image generator.
  // Rationale: Nano Banana occasionally reproduced NCERT-copyrighted
  // textbook pages verbatim. To eliminate any IP risk, we now route
  // students to the human-curated Graphics gallery instead — every
  // asset there was reviewed + approved by our team. See the "Need
  // more help? Refer to our Graphics section →" link under long AI
  // replies in the transcript.

  // Soft "let's actually start studying together" nudge after a few exchanges
  // when the user hasn't picked a clear topic yet. Genuine friend energy.
  useEffect(() => {
    if (studyNudgeShown) return;
    const userMsgs = messages.filter(m => m.role === 'user').length;
    if (userMsgs >= 4 && !headerTopic && !selectedTopic) {
      buddyEvents.say(
        "Loving this chat! \u2014 want to lock in a topic and study together? Pick one above \u2191",
        'happy', 7000
      );
      setStudyNudgeShown(true);
    }
  }, [messages, headerTopic, selectedTopic, studyNudgeShown]);
  const [showVoiceOverlay, setShowVoiceOverlay] = useState(false);
  const messagesEndRef = useRef(null);
  const chatScrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const syllabusInputRef = useRef(null);

  // School syllabus (PDF / DOCX) → AI reads it → plannable chapters.
  const [syllabusFile, setSyllabusFile] = useState(null);
  const [syllabusOpen, setSyllabusOpen] = useState(false);
  const [snapOpen, setSnapOpen] = useState(false);
  const [snapFile, setSnapFile] = useState(null);
  const handleSyllabusPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setSnapFile(file);
    setSnapOpen(true);
  };
  const textareaRef = useRef(null);

  const canUseVoice = user?.role === 'admin' || ['growth', 'premium', 'elite'].includes(user?.subscription_tier);
  const [voiceMode, setVoiceMode] = useState('browser'); // 'browser' | 'premium'

  // Handle voice transcript — send to chat and return AI response text
  const handleVoiceTranscript = async (text) => {
    if (!activeSession && !headerTopic) return null;
    let sessionId = activeSession?.session_id;
    if (!sessionId) {
      try {
        const d = await apiPost('/chat/sessions', { subject: activeSubject, topic: headerTopic });
        setActiveSession(d);
        sessionId = d.session_id;
        setMessages([{ message_id: 'greeting', role: 'assistant', content: d.greeting }]);
      } catch { return null; }
    }
    try {
      const res = await apiPost(`/chat/sessions/${sessionId}/messages`, {
        content: text, eli5_mode: false,
        current_subject: activeSubject || undefined,
        current_topic: headerTopic || undefined,
      });
      if (res.ai_message) {
        setMessages(prev => [...prev, { message_id: `user_${Date.now()}`, role: 'user', content: text }, res.ai_message]);
        return res.ai_message.content;
      }
    } catch { toast.error('Failed to send voice message'); }
    return null;
  };

  // Reset chat when subject changes or reset key changes (Home/Logo click)
  useEffect(() => {
    setMessages([]);
    setActiveSession(null);
    setInput('');
    setSelectedTopic('');
    setImagePreview(null);
    setImageBase64(null);
  }, [chatResetKey]);

  // Phase 2.65 — Study Buddy mid-chat subject/chapter swap warning.
  // If the student switches the top-bar Subject OR Chapter dropdown WHILE
  // an active chat session is open AND the session was opened on a
  // different subject/topic, inject a synthetic buddy bubble so the
  // change is impossible to miss. The buddy itself will also call this
  // out the next time the student sends a message (backend handles that
  // via current_subject/current_topic params).
  const lastSwapWarningRef = useRef('');
  const activeSessionRef = useRef(null);
  activeSessionRef.current = activeSession;
  const swapResetKeyRef = useRef(chatResetKey);
  // While a loaded / linked chat is still pulling the header onto its own
  // chapter, the header lagging behind is NOT the student switching.
  const syncingRef = useRef(null);
  useEffect(() => {
    // A header change that also reset the chat (New Chat) needs no warning.
    if (swapResetKeyRef.current !== chatResetKey) { swapResetKeyRef.current = chatResetKey; return; }
    if (!activeSession) return;
    if (!activeSubject && !headerTopic) return;
    const subjChanged = activeSubject && !sameName(activeSubject, activeSession.subject);
    const topicChanged = headerTopic && activeSession.topic && !sameName(headerTopic, activeSession.topic);
    const sync = syncingRef.current;
    if (sync && sync.sessionId === activeSession.session_id) {
      if (!subjChanged && !topicChanged) { syncingRef.current = null; return; }
      if (Date.now() < sync.until) return;
      syncingRef.current = null;
    }
    if (!subjChanged && !topicChanged) return;
    // De-dupe — only warn once per (subject, topic) combination.
    const key = `${activeSubject}::${headerTopic}::${activeSession.session_id}`;
    if (lastSwapWarningRef.current === key) return;
    lastSwapWarningRef.current = key;
    const fromLabel = `${activeSession.subject || 'this subject'} · ${activeSession.topic || 'this chapter'}`;
    const toLabel = `${activeSubject || activeSession.subject} · ${headerTopic || activeSession.topic}`;
    setMessages(prev => [...prev, {
      message_id: `swap_${Date.now()}`,
      role: 'assistant',
      // Friendly-but-firm Study Buddy nudge. Mirrors the same tone the
      // backend will use on the next actual reply.
      content:
        `Hold on — you just switched from **${fromLabel}** to **${toLabel}** while we're still mid-chat. ` +
        `I'll do my best, but switching subjects/chapters mid-session muddles things up. ` +
        `For a brand-new chapter, please tap **New Chat** so we can start fresh — promise it'll be cleaner for both of us! \uD83D\uDCAA`,
      created_at: new Date().toISOString(),
      synthetic_swap: true,
    }]);
  }, [activeSubject, headerTopic, activeSession, chatResetKey]);

  // Load topics for active subject (board-aware)
  useEffect(() => {
    if (activeSubject && user?.class_level) {
      setSelectedTopic('');
      const boardParam = user?.board ? `&board=${encodeURIComponent(user.board)}` : '';
      apiGet(`/onboarding/topics?subject=${encodeURIComponent(activeSubject)}&class_level=${user.class_level}${boardParam}`)
        .then(d => { setTopics(d.topics || []); if (d.topics?.length > 0) setSelectedTopic(d.topics[0].name); })
        .catch(() => {});
    }
  }, [activeSubject, user?.class_level, user?.board]);

  // Load recent sessions (reload when chat resets)
  useEffect(() => {
    apiGet('/chat/sessions').then(d => setRecentSessions((d.sessions || []).slice(0, 5))).catch(() => {});
  }, [chatResetKey]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // Grow the box with what's typed so the student can read all of it.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    el.style.overflowY = el.scrollHeight > 160 ? 'auto' : 'hidden';
  }, [input]);

  const lastAiId = [...messages].reverse().find(m => m.role === 'assistant' && m.message_id && !m.streaming && !/^(swap|limit)_/.test(m.message_id))?.message_id;

  // Tell Study Buddy a chat is on, so its Take Note button shows.
  const chatOn = !!activeSession?.session_id && messages.some(m => m.role === 'assistant' && m.message_id);
  useEffect(() => {
    const detail = chatOn ? { sessionId: activeSession.session_id, subject: activeSession.subject, chapter: activeSession.topic } : null;
    window.dispatchEvent(new CustomEvent(NOTE_EVENTS.context, { detail }));
  }, [chatOn, activeSession?.session_id, activeSession?.subject, activeSession?.topic]);
  useEffect(() => () => window.dispatchEvent(new CustomEvent(NOTE_EVENTS.context, { detail: null })), []);

  const startChatAndSend = async (messageText) => {
    // Iter 74 — the door is open. No subject picked? Start an "ask me
    // anything" chat and let the buddy work out where to take them.
    const topic = activeSubject ? (headerTopic || selectedTopic || activeSubject) : '';
    setSending(true);
    beginThinking();
    buddyEvents.thinking();
    try {
      const session = await apiPost('/chat/sessions', { subject: activeSubject || '', topic });
      setActiveSession(session);
      setMessages([{ message_id: 'greeting', role: 'assistant', content: session.greeting, created_at: new Date().toISOString() }]);

      // Send the user's message
      const userMsg = { message_id: `user_${Date.now()}`, role: 'user', content: messageText, created_at: new Date().toISOString() };
      setMessages(prev => [...prev, userMsg]);

      const streamId = `stream_${Date.now()}`;
      let typed = '';
      const res = await streamChatMessage(
        session.session_id,
        { content: messageText, eli5_mode: eli5, image_base64: imageBase64 || undefined },
        (delta) => {
          if (!typed) firstWordArrived();
          typed += delta;
          setMessages(prev => {
            const rest = prev.filter(m => m.message_id !== streamId);
            return [...rest, {
              message_id: streamId, role: 'assistant', content: typed,
              streaming: true, created_at: new Date().toISOString(),
            }];
          });
        },
      );
      setMessages(prev => {
        const filtered = prev.filter(m => m.message_id !== streamId);
        return [...filtered.slice(0, -1), res.user_message, res.ai_message];
      });
      setImageBase64(null); setImagePreview(null);
      buddyEvents.explaining();
    } catch (e) {
      setMessages(prev => prev.filter(m => !m.message_id.startsWith('stream_')));
      const isLimit = e?.status === 429 || /limit/i.test(e?.message || '');
      if (isLimit) {
        setMessages(prev => {
          const filtered = prev.filter(m => m.message_id !== `user_${Date.now()}`);
          return [...filtered, {
            message_id: `limit_${Date.now()}`,
            role: 'assistant',
            content: '__paywall__',
            paywall: true,
            created_at: new Date().toISOString(),
          }];
        });
        buddyEvents.say(
          "Hey, you\u2019ve used today's free chats \u2014 I'm here, I just can't reply more until tomorrow. \uD83D\uDC9B Upgrade to Growth and we can talk anytime.",
          'consoling', 9000, 'upgrade'
        );
      } else {
        toast.error('Couldn\u2019t reach me just now. Try again in a moment.');
        buddyEvents.console("That didn\u2019t go through. Let\u2019s try once more.");
      }
    }
    setSending(false);
  };

  // Thumbs-down quietly flags the reply AND asks the stronger model to try
  // again, so the child gets a better explanation instead of a dead end.
  const rateMessage = async (messageId, helpful) => {
    if (!messageId || String(messageId).startsWith('temp_') || messageId === 'greeting') return;
    setRating(r => ({ ...r, [messageId]: helpful ? 'up' : 'down' }));
    try {
      const res = await apiPost(`/chat/messages/${messageId}/rate`, { helpful });
      if (res?.retry) {
        setRating(r => ({ ...r, [messageId]: 'retried' }));
        setMessages(prev => (prev.some(m => m.message_id === res.retry.message_id)
          ? prev : [...prev, res.retry]));
        toast.success(res.ack_message || 'Let me explain that again.');
      } else if (!helpful) {
        toast.message(res?.ack_message || 'Thank you for telling us.');
      }
    } catch {
      if (!helpful) toast.error('Could not send that just now');
    }
  };

  // `override` lets a topic button send its own text without touching the box.
  const sendMessage = async (override) => {
    const typedText = typeof override === 'string' ? override : input;
    if (!typedText.trim() && !imageBase64) return;
    const messageText = typedText.trim();
    if (typeof override !== 'string') setInput('');

    if (!activeSession) {
      await startChatAndSend(messageText);
      return;
    }

    setSending(true);
    setMessages(prev => [...prev, { message_id: `temp_${Date.now()}`, role: 'user', content: messageText || '(Image uploaded)', created_at: new Date().toISOString() }]);
    const imgB64 = imageBase64;
    setImageBase64(null); setImagePreview(null);
    beginThinking();
    buddyEvents.thinking();

    try {
      const streamId = `stream_${Date.now()}`;
      let typed = '';
      const res = await streamChatMessage(
        activeSession.session_id,
        {
          content: messageText, eli5_mode: eli5, image_base64: imgB64 || undefined,
          // Top-bar dropdown values RIGHT NOW. If the student switched
          // subject / chapter mid-chat, the buddy will call it out clearly
          // in its reply.
          current_subject: activeSubject || undefined,
          current_topic: headerTopic || undefined,
        },
        (delta) => {
          // The buddy types into a placeholder bubble as the words arrive.
          if (!typed) firstWordArrived();
          typed += delta;
          setMessages(prev => {
            const rest = prev.filter(m => m.message_id !== streamId);
            return [...rest, {
              message_id: streamId, role: 'assistant', content: typed,
              streaming: true, created_at: new Date().toISOString(),
            }];
          });
        },
      );
      setMessages(prev => {
        const filtered = prev.filter(m => !m.message_id.startsWith('temp_') && m.message_id !== streamId);
        return [...filtered, res.user_message, res.ai_message];
      });
      buddyEvents.explaining();
    } catch (e) {
      setMessages(prev => prev.filter(m => !m.message_id.startsWith('stream_')));
      // 429 = daily limit hit. Replace silent failure with an empathetic, in-chat
      // message from the buddy that names the situation and offers the upgrade.
      const isLimit = e?.status === 429 || /limit/i.test(e?.message || '');
      if (isLimit) {
        setMessages(prev => {
          const filtered = prev.filter(m => !m.message_id.startsWith('temp_'));
          return [...filtered, {
            message_id: `limit_${Date.now()}`,
            role: 'assistant',
            content: '__paywall__',
            paywall: true,
            created_at: new Date().toISOString(),
          }];
        });
        buddyEvents.say(
          "Hey, you\u2019ve used today's free chats \u2014 I'm here, I just can't reply more until tomorrow. \uD83D\uDC9B Upgrade to Growth and we can talk anytime.",
          'consoling', 9000, 'upgrade'
        );
      } else {
        toast.error('Couldn\u2019t reach me just now. Try again in a moment.');
        buddyEvents.console("That didn\u2019t go through. Let\u2019s try once more.");
      }
    }
    setSending(false);
  };

  // Snap & Solve (and any deep link) can hand us a session id to open.
  const openedFromUrl = useRef('');
  useEffect(() => {
    const wanted = searchParams.get('session');
    if (!wanted || openedFromUrl.current === wanted) return;
    openedFromUrl.current = wanted;
    loadSessionRef.current?.(wanted);
    searchParams.delete('session');
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const loadSessionRef = useRef(null);

  const loadSession = async (sessionId) => {
    try {
      const d = await apiGet(`/chat/sessions/${sessionId}/messages`);
      if (d.session) syncingRef.current = { sessionId: d.session.session_id, until: Date.now() + 8000 };
      setActiveSession(d.session);
      setMessages(d.messages || []);
      const retried = new Set((d.messages || []).map(m => m.retry_of).filter(Boolean));
      setRating(Object.fromEntries((d.messages || [])
        .filter(m => m.rating || retried.has(m.message_id))
        .map(m => [m.message_id, retried.has(m.message_id) ? 'retried' : m.rating])));
      // Sync header dropdowns to match loaded session
      if (onSessionLoad && d.session) {
        onSessionLoad(d.session.subject, d.session.topic);
      }
    } catch { toast.error('Failed to load session'); }
  };
  loadSessionRef.current = loadSession;

  // Attach a photo straight to the chat (a question to solve).
  const attachAsQuestion = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => { setImageBase64(reader.result.split(',')[1]); setImagePreview(reader.result); };
    reader.readAsDataURL(file);
  };

  // Snap & Solve reads first and then asks what the child wants: file the
  // chapter, switch to it, or talk about it right now.
  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setSnapFile(file);
    setSnapOpen(true);
  };

  const getSummary = async () => {
    if (!activeSession) return;
    try {
      const s = await apiPost(`/chat/sessions/${activeSession.session_id}/summary`, {});
      setSummary(s); setShowSummary(true);
      confetti({ particleCount: 60, spread: 50, origin: { y: 0.5 } });
    } catch { toast.error('Could not generate summary'); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const hasMessages = messages.length > 0;

  // Composer — one definition, rendered centered in the welcome state and
  // pinned to the viewport bottom once a conversation is running.
  const composer = (
    <div
      className={hasMessages
        ? 'flex-shrink-0 border-t border-slate-100 bg-white px-3 sm:px-6 py-2 sm:py-3'
        : 'bg-white rounded-3xl border border-slate-200 shadow-sm px-3 sm:px-4 py-3'}
      data-testid="chat-input-area">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-end gap-2 sm:gap-3">
          <div className="flex-1 bg-slate-50 rounded-2xl border border-slate-200 px-4 py-3 focus-within:border-blue-300 focus-within:ring-1 focus-within:ring-blue-100 transition-all">
            <textarea
              ref={textareaRef}
              data-testid="chat-input"
              rows={1}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={mode === 'ai_tutor'
                ? 'Ask me anything about today\u2019s session\u2026'
                : 'Ask me anything \u2014 your chapter, or whatever you\u2019re curious about\u2026'}
              disabled={sending}
              className="w-full bg-transparent text-sm font-body text-slate-700 placeholder-slate-400 outline-none resize-none min-h-[24px] max-h-[160px] leading-6"
            />
          </div>
          <input type="file" ref={fileInputRef} accept="image/*" className="hidden" onChange={handleImageUpload} />
          <Hint text={HELP.study.snapSolve} side="top">
            <button data-testid="snap-solve-btn" onClick={() => fileInputRef.current?.click()}
              className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-emerald-400 flex items-center justify-center text-white flex-shrink-0 hover:scale-105 transition-transform shadow-sm">
              <Camera className="w-4.5 h-4.5" />
            </button>
          </Hint>
          <input type="file" ref={syllabusInputRef} accept=".pdf,.doc,.docx" className="hidden" onChange={handleSyllabusPick} />
          <Hint text="Upload your school syllabus (PDF or Word) — I'll read it and turn it into an exam plan" side="top">
            <button data-testid="syllabus-upload-btn" onClick={() => syllabusInputRef.current?.click()}
              className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center text-white flex-shrink-0 hover:scale-105 transition-transform shadow-sm">
              <Paperclip className="w-4.5 h-4.5" />
            </button>
          </Hint>
          {/* Browser Voice: available to every user, unlimited English, tied to chat limits only */}
          <Hint text={HELP.study.voiceBrowser} side="top">
            <button data-testid="voice-browser-btn" onClick={() => { setVoiceMode('browser'); setShowVoiceOverlay(true); }}
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all shadow-sm bg-gradient-to-br from-sky-500 to-blue-500 hover:scale-105 text-white">
              <Mic className="w-4.5 h-4.5" />
            </button>
          </Hint>
          {/* Premium Voice (ElevenLabs): Growth/Premium only, regional languages, 10 min/month */}
          {canUseVoice && (
            <Hint text={HELP.study.voicePremium} side="top">
            <button data-testid="voice-premium-btn" onClick={() => { setVoiceMode('premium'); setShowVoiceOverlay(true); }}
              className="relative w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all shadow-sm bg-gradient-to-br from-purple-500 via-fuchsia-500 to-amber-400 hover:scale-105 text-white overflow-hidden">
              {/* Orb icon — gradient sphere with glossy highlight for premium voice */}
              <svg viewBox="0 0 24 24" className="w-5 h-5 relative z-10" aria-hidden="true">
                <defs>
                  <radialGradient id="orb-grad-premium" cx="35%" cy="30%" r="75%">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
                    <stop offset="35%" stopColor="#fef3c7" stopOpacity="0.95" />
                    <stop offset="70%" stopColor="#f0abfc" stopOpacity="0.85" />
                    <stop offset="100%" stopColor="#7c3aed" stopOpacity="0.9" />
                  </radialGradient>
                  <radialGradient id="orb-shine-premium" cx="30%" cy="25%" r="22%">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
                  </radialGradient>
                </defs>
                <circle cx="12" cy="12" r="9" fill="url(#orb-grad-premium)" stroke="#ffffff" strokeOpacity="0.6" strokeWidth="0.5" />
                <ellipse cx="9" cy="8.5" rx="3" ry="2.2" fill="url(#orb-shine-premium)" />
              </svg>
              {/* Pulsing outer ring for distinction */}
              <span className="absolute inset-0 rounded-xl border-2 border-white/30 animate-pulse" />
              <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-amber-300 flex items-center justify-center shadow ring-2 ring-white">
                <Sparkles className="w-2 h-2 text-amber-700" />
              </span>
            </button>
            </Hint>
          )}
          <Hint text={HELP.study.send} side="top" align="end">
            <button data-testid="chat-send-btn" onClick={() => sendMessage()}
              disabled={sending || (!input.trim() && !imageBase64)}
              className="w-10 h-10 rounded-xl bg-blue-500 hover:bg-blue-600 flex items-center justify-center text-white flex-shrink-0 disabled:opacity-40 transition-all shadow-sm">
              <Send className="w-4.5 h-4.5" />
            </button>
          </Hint>
        </div>
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-xs text-slate-400 font-body">Simple mode</span>
            <Hint text={HELP.study.simpleMode} side="top" align="start">
              <Switch data-testid="eli5-toggle" checked={eli5} onCheckedChange={setEli5}
                className="data-[state=checked]:bg-blue-500 scale-75" />
            </Hint>
          </div>
          <span className="text-[10px] text-slate-300 font-body hidden sm:inline">
            ↵ send &middot; ⇧↵ new line
          </span>
        </div>
      </div>
    </div>
  );

  return (
    /* Iter 60j — canonical chat-app shell using `absolute inset-0`.
       Fills the visible main viewport exactly regardless of browser
       zoom, so the composer is ALWAYS at the true viewport bottom.
       - Root: absolute inset-0 flex flex-col
       - Content: flex-1 min-h-0 overflow-y-auto (scrolls when tall)
       - Composer: flex-shrink-0 sibling (glued to viewport bottom)
       Mirrors WhatsApp Web / ChatGPT / Telegram — no 100vh math, no
       sticky tricks, works at any zoom level. */
    <div className="absolute inset-0 flex" data-testid="home-page">
      {/* Premium layer: hidden entirely when the Premium switch is off, and
          isolated so a Premium failure can never blank this screen. */}
      {!premiumOff && (
        <PremiumBoundary>
          <StudySidebar
            mode={mode || 'explore'}
            sessions={recentSessions}
            activeSessionId={activeSession?.session_id}
            onOpenHistory={() => setHistoryOpen(true)}
            onNewChat={() => {
              setActiveSession(null); setMessages([]); setInput('');
              setGuided(null);
            }}
            onChangeMode={() => setPickerOpen(true)}
            onOpenSession={loadSession}
          />
        </PremiumBoundary>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
      <SyllabusMappingPanel enabled={isStudent && !!mode && !pickerOpen && !profileOpen && !needsAcademicProfile(user)}
        onUpload={subject => { setUploadSubject(subject); setSyllabusFile(null); setSyllabusOpen(true); }}
        onEditProfile={() => setProfileOpen(true)} />
      {/* Main Content */}
      <div className="flex-1 min-h-0 overflow-y-auto" ref={chatScrollRef}>
        <SelectionSaver containerRef={chatScrollRef} sessionId={activeSession?.session_id} />
        {!hasMessages ? (
          /* Welcome State — centered hero + composer (premium AI-assistant feel) */
          <div className="min-h-full flex flex-col items-center justify-center px-4 sm:px-6 py-4">
            <div className="max-w-2xl w-full space-y-4 -mt-6">
              <div className="mb-1">
                <BuddyChatGreeting onPickFriend={() => setRosterOpen(true)} />
              </div>

              <div className="flex justify-center gap-2 -mt-1">
                <button data-testid="chat-history-open" onClick={() => setHistoryOpen(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/80 border border-slate-200 text-[11px] font-heading text-slate-500 hover:text-slate-800 hover:border-slate-300 transition-all">
                  <History className="w-3 h-3" /> Your chats
                </button>
                <button data-testid="replay-welcome-film" onClick={() => setFilmReplay(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/80 border border-slate-200 text-[11px] font-heading text-slate-500 hover:text-amber-700 hover:border-amber-300 transition-all">
                  <Sparkles className="w-3 h-3" /> Welcome film
                </button>
              </div>

              <TodaysMissionCard onStart={(subject, chapter) => {
                // The subject lives in the header selector (owned by the shell),
                // so we set the chapter + mode and nudge if the subject differs.
                if (chapter) setSelectedTopic(chapter);
                setMode(MODES.EXAM);
                toast.message(subject && activeSubject && subject !== activeSubject
                  ? `Pick ${subject} in the subject bar above and I'll start the session`
                  : 'Starting your study session…');
              }} />

              {composer}

              {premiumOff ? (
                topics.length > 0 && (
                  <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2" data-testid="topic-chip-row">
                    {topics.slice(0, 6).map(t => (
                      <button key={t.topic_id} data-testid={`topic-chip-${t.name.replace(/\s/g, '-').toLowerCase()}`}
                        onClick={() => { setSelectedTopic(t.name); setInput(`Explain ${t.name} to me`); }}
                        className="px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full bg-white border border-slate-200 text-[11px] sm:text-xs font-body text-slate-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600 transition-all">
                        {t.name}
                      </button>
                    ))}
                  </div>
                )
              ) : (
                <PremiumBoundary>
                {mode === MODES.EXAM ? (
                examReady && !examReady.ready ? (
                  <button type="button" onClick={() => setExamGateOpen(true)}
                    data-testid="exam-mode-blocked-card"
                    className="w-full rounded-2xl border-2 border-dashed border-amber-300 bg-amber-50/60 p-4 text-left hover:bg-amber-50 transition-colors">
                    <p className="font-heading text-sm font-semibold text-slate-800">
                      {examReady.issue?.title || 'Exam Mode needs one more thing'}
                    </p>
                    <p className="text-[11px] text-slate-600 font-body mt-1">{examReady.issue?.detail}</p>
                    <span className="inline-block mt-2 text-[11px] font-heading text-amber-800 underline">
                      {examReady.issue?.cta_label || 'Set it up'}
                    </span>
                  </button>
                ) : guided ? (
                  <GuidedSessionPanel session={guided} onSessionUpdate={setGuided} />
                ) : (
                  <div className="rounded-2xl border-2 border-dashed border-fuchsia-200 bg-fuchsia-50/40 p-4 text-center" data-testid="guided-session-empty">
                    <p className="font-heading text-sm font-semibold text-slate-800">
                      {guidedLoading || !examReady ? 'Building today\u2019s guided session\u2026'
                        : `Opening ${examReady.focus?.subject || ''} \u00b7 ${examReady.focus?.topic || ''}\u2026`}
                    </p>
                    <p className="text-[11px] text-slate-500 font-body mt-1">
                      I&rsquo;ll line up the cinegraphic, infographic, explanation, flashcards and mastery quiz for you.
                    </p>
                  </div>
                )
              ) : (
                topics.length > 0 && (
                  <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2" data-testid="topic-chip-row">
                    {topics.slice(0, 6).map(t => (
                      <button key={t.topic_id} data-testid={`topic-chip-${t.name.replace(/\s/g, '-').toLowerCase()}`}
                        onClick={() => { setSelectedTopic(t.name); setInput(`Explain ${t.name} to me`); }}
                        className="px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full bg-white border border-slate-200 text-[11px] sm:text-xs font-body text-slate-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600 transition-all">
                        {t.name}
                      </button>
                    ))}
                  </div>
                )
              )}
                </PremiumBoundary>
              )}
            </div>
          </div>
        ) : (
          /* Chat Messages */
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
            {activeSession && (
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
                <div className="min-w-0">
                  <h2 className="font-heading text-base font-semibold text-slate-800 truncate">
                    {activeSession.title || activeSession.topic || 'Ask me anything'}
                  </h2>
                  <p className="text-xs text-slate-400 font-body truncate">
                    {activeSession.subject || 'Open chat with your Study Buddy'}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Hint text="Every chat you've had — named, searchable, kept 30 days" side="bottom" align="end">
                    <button data-testid="chat-history-btn" onClick={() => setHistoryOpen(true)}
                      className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors">
                      <History className="w-4 h-4 text-slate-500" />
                    </button>
                  </Hint>
                  <button data-testid="new-chat-btn" onClick={() => { setActiveSession(null); setMessages([]); }}
                    className="text-xs text-blue-500 font-body hover:underline">New Chat</button>
                  <button data-testid="summary-btn" onClick={getSummary}
                    className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center hover:bg-amber-100 transition-colors">
                    <Sparkles className="w-4 h-4 text-amber-500" />
                  </button>
                </div>
              </div>
            )}
            <div className="space-y-4">
              {messages.map((msg, i) => {
                if (msg.paywall) {
                  return (
                    <div key={msg.message_id || i} className="flex justify-start animate-slide-up" data-testid="chat-paywall-card">
                      <div className="max-w-[90%] rounded-3xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 via-rose-50 to-fuchsia-50 px-5 py-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                            <Sparkles className="w-5 h-5 text-amber-500" />
                          </div>
                          <div className="flex-1">
                            <p className="font-heading text-sm font-bold text-slate-900 mb-1">You've used today's free chats</p>
                            <p className="text-sm font-body text-slate-700 leading-relaxed mb-3">
                              I'm right here, I just can't reply more on your free trial today. Resets at midnight \u2014
                              or subscribe to <strong>Growth</strong> and we can talk anytime, no limits.
                            </p>
                            <div className="flex flex-wrap gap-2">
                              <button data-testid="paywall-upgrade-btn" onClick={() => navigate('/subscription')}
                                className="px-4 py-2 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-heading hover:shadow-md transition-all">
                                Upgrade to Growth
                              </button>
                              <button data-testid="paywall-refer-btn" onClick={() => navigate('/refer')}
                                className="px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 text-xs font-heading hover:border-emerald-300 hover:text-emerald-700 transition-all">
                                Or earn it free \u2014 invite a friend
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }
                return (
                <div key={msg.message_id || i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-slide-up`}>
                  <div className={`max-w-[82%] px-5 py-4 ${msg.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-ai'}`}
                    {...(msg.role === 'assistant' && msg.message_id && !msg.streaming && !/^(swap|limit)_/.test(msg.message_id) ? { 'data-ai-message-id': msg.message_id } : {})}>
                    {msg.role === 'user' && msg.image_base64 && (
                      <img
                        data-testid={`chat-msg-image-${msg.message_id || i}`}
                        src={msg.image_base64.startsWith('data:') ? msg.image_base64 : `data:image/jpeg;base64,${msg.image_base64}`}
                        alt="Uploaded"
                        className="mb-2 max-w-full max-h-72 rounded-xl border border-white/30 object-contain"
                      />
                    )}
                    <div className="text-sm font-body text-slate-700 leading-relaxed chat-content">
                      <FormattedMessage content={msg.content} />
                      {msg.streaming && (
                        <span className="inline-block w-[2px] h-[1.05em] ml-0.5 align-[-0.15em] bg-slate-400 animate-pulse" aria-hidden="true" />
                      )}
                    </div>
                    {msg.role === 'assistant' && msg.topics?.length > 0 && (
                      <TopicChips topics={msg.topics} messageId={msg.message_id || i}
                        disabled={sending} onPick={(t) => sendMessage(`Teach me: ${t}`)} />
                    )}
                    {msg.role === 'assistant' && msg.choices?.length > 0 && (
                      <AnswerChoices choices={msg.choices} messageId={msg.message_id || i}
                        disabled={sending || msg.message_id !== lastAiId} onPick={(c) => sendMessage(`My answer: ${c}`)} />
                    )}
                    {msg.role === 'assistant' && msg.guide?.length > 0 && (
                      <GuideChips guide={msg.guide}
                        subject={activeSubject || activeSession?.subject}
                        topic={headerTopic || selectedTopic || activeSession?.topic} />
                    )}
                    {msg.role === 'assistant' && msg.illustration && (msg.illustration.image_url || msg.illustration.image_base64) && (
                      <WatermarkedMedia className="mt-3 block">
                        <figure className="rounded-xl overflow-hidden border border-slate-200/70 bg-white/60" data-testid={`chat-msg-illustration-${msg.message_id || i}`}>
                          <img
                            src={msg.illustration.image_base64 || msg.illustration.image_url}
                            alt={msg.illustration.caption || 'Illustration'}
                            className="block w-full max-h-80 object-contain bg-slate-50"
                            loading="lazy"
                          />
                          {msg.illustration.caption && (
                            <figcaption className="text-[10px] text-slate-500 font-body px-3 py-1.5 bg-slate-50/70 border-t border-slate-100 flex items-center justify-between gap-2">
                              <span className="truncate">{msg.illustration.caption}</span>
                              <span className="flex-shrink-0 inline-flex items-center gap-1 text-[9px] uppercase tracking-wider text-slate-400">
                                {msg.illustration.source === 'syllabus_page' ? 'Textbook page' : msg.illustration.source === 'image_cache' ? 'Cached' : 'AI illustration'}
                              </span>
                            </figcaption>
                          )}
                        </figure>
                      </WatermarkedMedia>
                    )}
                    {msg.role === 'assistant'
                      && !msg.illustration
                      && !['app', 'personal'].includes(msg.scope)
                      && msg.message_id
                      && msg.message_id !== 'greeting'
                      && !String(msg.message_id).startsWith('temp_')
                      && msg.content && msg.content.length > 40 && (
                      /* Iter 60n (Aug 2026) — Image generation removed for
                         IP-compliance (Nano Banana was producing NCERT-
                         copyrighted textbook pages). Replaced with a soft
                         nudge to the curated Graphics gallery which only
                         serves human-vetted, admin-approved content. */
                      <button
                        type="button"
                        onClick={() => {
                          const params = new URLSearchParams();
                          if (activeSubject) params.set('subject', activeSubject);
                          if (headerTopic || selectedTopic) {
                            params.set('topic', headerTopic || selectedTopic);
                          }
                          const qs = params.toString();
                          navigate(qs ? `/gallery?${qs}` : '/gallery');
                        }}
                        data-testid={`chat-msg-goto-graphics-${msg.message_id || i}`}
                        className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-heading text-blue-600 hover:text-blue-700 hover:underline transition"
                      >
                        <ImagePlus className="w-3.5 h-3.5" />
                        Need more help? Refer to our Graphics section →
                      </button>
                    )}
                    {msg.role === 'assistant' && msg.message_id && msg.message_id !== 'greeting'
                      && !String(msg.message_id).startsWith('temp_') && !msg.streaming
                      && msg.content && msg.content.length > 40 && (
                      <div className="mt-2 flex items-center gap-1.5" data-testid={`chat-rate-${msg.message_id}`}>
                        <button type="button" aria-label="This helped" disabled={!!rating[msg.message_id]}
                          data-testid={`chat-rate-up-${msg.message_id}`}
                          onClick={() => rateMessage(msg.message_id, true)}
                          className={`p-1 rounded-full transition-colors ${rating[msg.message_id] === 'up' ? 'text-emerald-600 bg-emerald-50' : 'text-slate-300 hover:text-emerald-500'}`}>
                          <ThumbsUp className="w-3.5 h-3.5" />
                        </button>
                        <button type="button" aria-label="This did not help — try again" disabled={!!rating[msg.message_id]}
                          data-testid={`chat-rate-down-${msg.message_id}`}
                          onClick={() => rateMessage(msg.message_id, false)}
                          className={`p-1 rounded-full transition-colors ${rating[msg.message_id] === 'down' ? 'text-rose-600 bg-rose-50' : 'text-slate-300 hover:text-rose-500'}`}>
                          <ThumbsDown className="w-3.5 h-3.5" />
                        </button>
                        {rating[msg.message_id] === 'down' && (
                          <span className="text-[10px] font-body text-slate-400">Trying again properly…</span>
                        )}
                        {rating[msg.message_id] === 'retried' && (
                          <span className="text-[10px] font-body text-slate-400">Explained again below</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                );
              })}
              {sending && !streamStarted && (
                <div className="flex justify-start animate-slide-up" data-testid="chat-thinking">
                  <div className="chat-bubble-ai px-4 py-3">
                    <div className="flex gap-1.5">
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" />
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay:'0.1s'}} />
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay:'0.2s'}} />
                    </div>
                    {thinkingNote && (
                      <p className="text-[11px] font-body text-slate-500 mt-2 max-w-[18rem]"
                         data-testid="chat-thinking-note">{thinkingNote}</p>
                    )}
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>
        )}
      </div>

      {/* Image Preview */}
      {imagePreview && (
        <div className="max-w-3xl mx-auto w-full px-4 sm:px-6 pb-2">
          <div className="relative inline-block">
            <img src={imagePreview} alt="Upload" className="h-16 rounded-xl border border-slate-200" />
            <button data-testid="remove-image" onClick={() => { setImagePreview(null); setImageBase64(null); }}
              className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs">
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* Composer — centered in the welcome state (premium AI-assistant feel),
          glued to the viewport bottom once the conversation starts. */}
      {hasMessages && composer}
      </div>

      <ModePickerDialog
        open={pickerOpen && !introOpen && !filmOpen}
        modes={modes}
        blocking={!mode}
        step={1}
        totalSteps={5}
        studentName={user?.first_name || user?.name?.split(' ')[0] || ''}
        onPick={pickMode}
        onClose={() => { if (mode) setPickerOpen(false); }}
      />

      <AcademicProfileDialog open={academicOpen}
        step={2}
        totalSteps={5}
        onBack={() => setPickerOpen(true)}
        onClose={() => setProfileOpen(false)}
        onSaved={updated => {
          setProfileOpen(false);
          const nonCbse = !updated.board?.toLowerCase().includes('cbse');
          setUploadSubject(''); setSyllabusFile(null);
          // The theme step comes next — hold the syllabus prompt until it's done.
          if (needsThemeSetup && !themeStepDone) setPendingSyllabus(nonCbse);
          else if (nonCbse) setSyllabusOpen(true);
        }} />

      <ThemeDialog open={themeStepOpen} setup step={3} totalSteps={5}
        onOpenChange={() => {}}
        onBack={() => { setThemeStepOpen(false); setProfileOpen(true); }}
        target={{ kind: 'self', name: user?.first_name || user?.name?.split(' ')[0] || '', theme: user?.theme }}
        onSaved={theme => setUser({ ...user, theme, theme_setup_seen: true })}
        onDone={finishThemeStep} />

      <PlanChoiceDialog open={planStepOpen} step={4} totalSteps={5} onDone={finishPlanStep}
        onBack={() => { setPlanStepOpen(false); setThemeStepDone(false); setThemeStepOpen(true); }} />

      <SyllabusMappingStep open={mappingStepOpen} step={5} totalSteps={5}
        onBack={() => { setMappingStepOpen(false); setPlanStepDone(false); setPlanStepOpen(true); }}
        board={user?.board} classLevel={user?.class_level} school={user?.school}
        onConfirm={() => closeMappingStep(false)}
        onMismatch={() => closeMappingStep(true)} />

      <IntroVideoDialog open={introOpen}
        url={help?.intro_video_url || ''}
        title={help?.intro_video_title || 'Meet your Study Buddies'}
        skipLabel={help?.intro_video_skip_label || 'Skip intro'}
        onDone={finishIntro} />

      <WelcomeFilm open={filmOpen}
        studentName={user?.first_name || user?.name?.split(' ')[0] || ''}
        onDone={finishIntro} />

      <WelcomeFilm open={filmReplay && !filmOpen}
        studentName={user?.first_name || user?.name?.split(' ')[0] || ''}
        onDone={() => setFilmReplay(false)} />

      <ChatHistoryDrawer open={historyOpen} onOpenChange={setHistoryOpen}
        activeSessionId={activeSession?.session_id}
        onOpenSession={loadSession} />

      <SnapSolveDialog
        open={snapOpen}
        onOpenChange={(o) => { setSnapOpen(o); if (!o) setSnapFile(null); }}
        file={snapFile}
        onSolveAsQuestion={attachAsQuestion}
        onSwitchTo={({ subject, chapter }) => { onSessionLoad?.(subject, chapter); setSelectedTopic(chapter); toast.success(`Switched to ${subject} · ${chapter}`); }}
      />
      <SyllabusUploadDialog
        open={syllabusOpen}        onOpenChange={(o) => { setSyllabusOpen(o); if (!o) setSyllabusFile(null); }}
        file={syllabusFile}
        defaults={{
          board: user?.board || '',
          class_level: user?.class_level ? String(user.class_level) : '',
          subject: uploadSubject || activeSubject || '',
          state: user?.state || '',
          city: user?.city || '',
        }}
      />

      {showSummary && summary && <AhaSummaryCard summary={summary} onClose={() => setShowSummary(false)} />}
      {showVoiceOverlay && <VoiceAIOverlay isOpen={showVoiceOverlay} onClose={() => setShowVoiceOverlay(false)} onTranscript={handleVoiceTranscript} activeSubject={activeSubject} activeTopic={headerTopic} mode={voiceMode} />}
      <RosterPicker open={rosterOpen} onOpenChange={setRosterOpen} />
      <ExamModeWizard open={examWizardOpen} onOpenChange={setExamWizardOpen}
        onDone={() => { toast.success("Your plan is ready — today's mission is on your home screen"); setExamCheckSeq(n => n + 1); }} />
      <ExamModeGate open={examGateOpen} onOpenChange={setExamGateOpen} readiness={examReady}
        onAddExam={() => setExamWizardOpen(true)}
        onMapPortion={() => navigate('/exams')}
        onReady={() => setExamCheckSeq(n => n + 1)}
        onBackToExplore={() => pickMode(MODES.EXPLORE)} />
    </div>
  );
}
